// Utilitários de previsão para o Painel Inteligência.
// Mantidos puros (sem dependência de React/Supabase) para serem testáveis.

export interface TrendForecast {
  next: number      // projeção do próximo período
  slope: number     // inclinação da reta (variação por período)
}

// Regressão linear simples (mínimos quadrados) sobre uma série temporal
// igualmente espaçada. Retorna a projeção do próximo ponto e o slope.
export function linearTrendForecast(values: number[]): TrendForecast {
  const n = values.length
  if (n === 0) return { next: 0, slope: 0 }
  if (n === 1) return { next: values[0], slope: 0 }

  const xs = values.map((_, i) => i)
  const meanX = xs.reduce((a, b) => a + b, 0) / n
  const meanY = values.reduce((a, b) => a + b, 0) / n

  let num = 0, den = 0
  for (let i = 0; i < n; i++) {
    num += (xs[i] - meanX) * (values[i] - meanY)
    den += (xs[i] - meanX) ** 2
  }
  const slope = den === 0 ? 0 : num / den
  const intercept = meanY - slope * meanX
  const next = Math.max(0, intercept + slope * n)
  return { next, slope }
}

// Média móvel dos últimos `window` períodos.
export function movingAverage(values: number[], window = 3): number {
  if (values.length === 0) return 0
  const slice = values.slice(-window)
  return slice.reduce((a, b) => a + b, 0) / slice.length
}

/**
 * Fator máximo de inflar a demanda prevista sobre a média plana. Limita ruído de
 * uma série curta/esparsa (ex.: um pico isolado triplicar a compra). Tunável.
 */
export const DEMAND_FORECAST_CAP = 2
/** Mínimo de pontos mensais para confiar na tendência; abaixo disso usa a média plana. */
export const DEMAND_FORECAST_MIN_POINTS = 3

/**
 * Demanda mensal prevista para fins de COMPRA (anti-ruptura), a partir de uma série
 * mensal (mais antigo → mais recente) e da média plana atual.
 *
 * Viés "nunca reduzir": o resultado nunca é menor que a média plana — a previsão só
 * AUMENTA a compra de itens acelerando, nunca enfraquece a proteção de um item em
 * queda que pode reverter. Limitado a `flatAvg × cap` para conter ruído de série curta.
 *
 * Série curta (< MIN_POINTS) ou sem demanda (flatAvg ≤ 0) → devolve a média plana
 * (sem previsão). Não deve alimentar a cobertura/semáforo do Painel BX — só o ponto
 * de ressuprimento, a sugestão e o sinal de risco.
 */
export function predictMonthlyDemand(
  series: number[],
  flatAvg: number,
  cap: number = DEMAND_FORECAST_CAP,
): number {
  if (flatAvg <= 0) return flatAvg
  if (series.length < DEMAND_FORECAST_MIN_POINTS) return flatAvg
  const projection = linearTrendForecast(series).next
  // Piso = média plana (nunca reduz); teto = média plana × cap (contém ruído).
  return Math.min(Math.max(projection, flatAvg), flatAvg * cap)
}

export interface SeasonalIndex { month_num: number; index: number }

// Índice sazonal por mês: avg_qty do mês ÷ média geral.
// index > 1 → mês acima da média; < 1 → abaixo.
export function seasonalIndices(points: { month_num: number; avg_qty: number }[]): SeasonalIndex[] {
  if (points.length === 0) return []
  const overall = points.reduce((a, p) => a + p.avg_qty, 0) / points.length
  if (overall === 0) return points.map(p => ({ month_num: p.month_num, index: 0 }))
  return points.map(p => ({ month_num: p.month_num, index: p.avg_qty / overall }))
}
